import Vue from 'vue'
import deleteRecord from '@/utils/delete-record'
import findIndex from 'lodash/findIndex'
import getCurrentUserId from '@/utils/get-current-user-id'

// API resources
import apiRealEstates from '@/api/modules/real-estates'
import apiUser from '@/api/modules/user'

// Mutations
import {
  ADD_REAL_ESTATE,
  ADD_REAL_ESTATE_ESTIMATE,
  DELETE_REAL_ESTATE,
  ADD_REAL_ESTATE_CUMULATIVE_HISTORY,
  RESET_MODULE
} from '@/store/mutation-types'

// Default state
const getDefaultState = () => {
  return {
    realEstates: [],
    histories: {},
    completeHistory: {}
  }
}
const state = getDefaultState()

// ----------------------------------------------------------------------

const actions = {
  fetchAmountHistoryAll (context, { userId = getCurrentUserId(), from, cached = true }) {
    if (cached && context.state.completeHistory.data) {
      return Promise.resolve(context.state.completeHistory)
    }

    return apiUser.fetchAmountHistoryAll(userId, from)
      .then(history => {
        context.commit(ADD_REAL_ESTATE_CUMULATIVE_HISTORY, history)
        return history
      })
  },

  update (context, { data, estimateEnabled = false }) {
    return apiRealEstates.update(data.id, data)
      .then(() => {
        return apiRealEstates.estimate(data.id, {
          estimateEnabled: estimateEnabled,
          assetPrice: data.currentAmount || data.amount
        })
          .then(({estimate}) => {
            context.dispatch('fetchRealEstateById', {id: data.id, cached: false})
            context.commit(ADD_REAL_ESTATE_ESTIMATE, {estimate: estimate, id: data.id})
          })
      })
  },

  updateAssetPrice (context, {data, estimateEnabled = false}) {
    return apiRealEstates.estimate(data.id, {estimateEnabled: estimateEnabled, assetPrice: data.assetPrice})
      .then(({estimate}) => {
        context.commit(ADD_REAL_ESTATE_ESTIMATE, {estimate: estimate, id: data.id})
      })
  },

  updateName (context, data) {
    return apiRealEstates.update(data.id, data)
      .then(() => context.dispatch('fetchRealEstateById', {id: data.id, cached: false}))
  },

  add (context, data) {
    return apiUser.addRealEstate(data)
      .then(({realEstate}) => {
        return apiRealEstates.estimate(realEstate.id, {estimateEnabled: true})
          .then(({estimate}) => {
            realEstate.estimate = estimate
            realEstate.amount = estimate.assetPrice
            context.commit(ADD_REAL_ESTATE, realEstate)
            return realEstate
          })
      })
  },

  addManual (context, data) {
    return apiUser.addRealEstate(data)
      .then(({realEstate}) => {
        return apiRealEstates.estimate(realEstate.id, {estimateEnabled: false, assetPrice: data.currentAmount})
          .then(({estimate}) => {
            realEstate.estimate = estimate
            realEstate.amount = estimate.history[estimate.history.length - 1][1]
            context.commit(ADD_REAL_ESTATE, realEstate)
            return realEstate
          })
      })
  },

  fetch (context, cached = true) {
    const { realEstates } = context.state
    if (cached && realEstates.length) {
      return Promise.resolve(realEstates)
    }

    return apiUser.fetchRealEstates()
      .then(({realEstates}) => {
        realEstates.forEach(realEstate => {
          context.commit(ADD_REAL_ESTATE, realEstate)
        })
      })
  },

  fetchByUser (context, { id, cached = true }) {
    const { realEstates } = context.state
    if (cached && realEstates.length) {
      return Promise.resolve(realEstates)
    }

    return apiUser.fetchRealEstatesByUser(id)
      .then(({realEstates}) => {
        realEstates.forEach(realEstate => {
          context.commit(ADD_REAL_ESTATE, realEstate)
        })
      })
  },

  fetchRealEstateById (context, { id, cached = true }) {
    let realEstate
    if (cached && (realEstate = context.getters.getById(id))) {
      return Promise.resolve(realEstate)
    }

    return apiRealEstates.fetchById(id)
      .then(({realEstate}) => {
        return apiRealEstates.fetchEstimate(id)
          .then(({estimate}) => {
            Vue.set(realEstate, 'estimate', estimate)
            realEstate.estimate = estimate
            context.commit(ADD_REAL_ESTATE, realEstate)
            return realEstate
          })
      })
  },

  fetchEstimate (context, { id, cached = true }) {
    const realEstate = context.getters.getById(id)
    if (cached && realEstate && realEstate.estimate) {
      return Promise.resolve('Estimate already cached')
    }

    return apiRealEstates.fetchEstimate(id)
      .then(({estimate}) => {
        context.commit(ADD_REAL_ESTATE_ESTIMATE, {estimate: estimate, id: parseInt(id)})
      })
  },

  delete (context, id) {
    return apiRealEstates.delete(id)
      .then(() => {
        context.commit(DELETE_REAL_ESTATE, id)
      })
  },

  resetState (context) {
    context.commit(RESET_MODULE)
  }
}

const mutations = {
  [ADD_REAL_ESTATE] (state, data) {
    const index = findIndex(state.realEstates, { id: data.id })
    if (index >= 0) {
      state.realEstates = [
        ...state.realEstates.slice(0, index),
        data,
        ...state.realEstates.slice(index + 1)
      ]
    } else {
      state.realEstates = [
        ...state.realEstates,
        data
      ]
    }
    state.realEstates.sort((a, b) => {
      return b.amount - a.amount
    })
  },

  [ADD_REAL_ESTATE_ESTIMATE] (state, data) {
    const index = findIndex(state.realEstates, { id: data.id })
    Vue.set(state.realEstates[index], 'estimate', data.estimate)
  },

  [DELETE_REAL_ESTATE] ({ realEstates }, id) {
    deleteRecord(realEstates, id)
  },

  [ADD_REAL_ESTATE_CUMULATIVE_HISTORY] (state, data) {
    Vue.set(state, 'completeHistory', data)
  },

  [RESET_MODULE] (state) {
    Object.assign(state, getDefaultState())
  }
}

const getters = {
  getById: ({ realEstates }) => (id) => {
    return realEstates.find(realEstate => parseInt(realEstate.id) === parseInt(id))
  },

  getGeocode: ({ realEstates }) => (id) => {
    let realEstate = realEstates.find(realEstate => realEstate.id === id)
    return realEstate.location
  },

  getHistoryById: ({ histories }) => (id) => histories[id],

  getFirstDate: ({ realEstates }) => (id) => {
    const { history } = realEstates.find(realEstate => realEstate.id === id).estimate
    return history[0] ? history[0][0] : Date.now()
  },

  getCumulativeValues: ({ realEstates }) => (key) => {
    return realEstates.reduce((sum, {[key]: value}) => sum + value, 0)
  }
}

export default () => ({
  namespaced: true,
  state,
  actions,
  mutations,
  getters
})
